import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTabsModule } from '@angular/material/tabs';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize, take, takeUntil } from 'rxjs/operators';
import {
  BUSINESS_SERVICE_INPUT,
  BUSINESS_TYPE,
  BusinessDetailsService,
  ComponentBase,
  GET_SERVICES_INPUT,
  SavePhasesDataType,
  SERVICE,
  SERVICE_FORM,
  SharedService,
  NumberOnlyDirective,
} from 'src/app/core';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { CurrencySvgDirective } from 'src/app/core/directives/currency-svg.directive';

@Component({
  selector: 'app-add-services',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    FlexLayoutModule,
    MatTabsModule,
    MatCheckboxModule,
    MatButtonModule,
    NumberOnlyDirective,
    CurrencySvgDirective,
  ],
  templateUrl: './add-services.component.html',
  styleUrl: './add-services.component.scss',
})
export class AddServicesComponent extends ComponentBase implements OnInit {
  @Input() titleKey = 'Add_Services';
  @Input() descriptionKey: string | undefined = 'Add_services_description';
  @Input() submitLabelKey = 'Confirm';
  @Input() cancelLabelKey = 'Cancel';

  /** When false, hide internal action buttons (used when embedded in sidenav). */
  @Input() showActions = true;

  @Output() confirm = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  servicesList: SERVICE[] = [];
  businessTypes: BUSINESS_TYPE[] = [];
  myDataCollection: SERVICE_FORM[] = [];
  currentBusinessTypeSelected: number | null = null;
  selectAllServiceChecked = false;

  haveTax = false;
  isPricesWithTax = false;

  /** Loader flag for services list while calling getServices */
  servicesLoading = false;

  private savePhasesDataType = SavePhasesDataType.Edit;

  /** Currency id from shared service response (nullable). */
  get currencyId(): string | number | null {
    const rawCurrency = this.sharedService.currency as
      | { Id?: string | number }
      | string
      | number
      | null
      | undefined;

    if (rawCurrency == null) return null;
    if (typeof rawCurrency === 'object') return rawCurrency.Id ?? null;
    return rawCurrency;
  }

  constructor(
    private readonly businessDetailsService: BusinessDetailsService,
    private readonly sharedService: SharedService,
    private readonly toastr: ToastrService,
    private readonly router: Router,
    readonly translateService: TranslateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadInitialData();
  }

  hasServicePriceError(service: SERVICE): boolean {
    if (!service.checked) return false;
    const p = service.ServicePrice;
    return p == null || p === undefined || Number(p) < 1;
  }

  get canConfirm(): boolean {
    if (!this.myDataCollection?.length || this.loading) return false;
    // At least one category with at least one selected service and all selected have prices
    for (const cat of this.myDataCollection) {
      if (!cat.RegistrationServices?.length) {
        continue;
      }
      const invalidPrice = cat.RegistrationServices.some(
        (s) =>
          s.ServicePrice == null ||
          s.ServicePrice === undefined ||
          Number(s.ServicePrice) < 1
      );
      if (invalidPrice) {
        return false;
      }
    }
    // If there is at least one category that has selected services, confirm is enabled
    return this.myDataCollection.some(
      (c) => c.RegistrationServices && c.RegistrationServices.length > 0
    );
  }

  private loadInitialData(): void {
    const regId = this.sharedService.getCookie('RegistrationCycleLogId');
    if (!regId) {
      this.toastr.warning(this.translateService.instant('Registration_Session_Expired'));
      this.router.navigate(['registration-cycle']);
      return;
    }

    this.haveTax = !!this.sharedService.businessDetails?.HaveTax;
    this.isPricesWithTax =
      !!this.sharedService.businessDetails?.HaveTax &&
      !!this.sharedService.businessDetails?.PriceIncludeTax;

    this.businessTypes = this.sharedService.basicDetails?.BusinessTypes ?? [];

    this.savePhasesDataType = SavePhasesDataType.Edit;
    if (this.sharedService.doneWorkHoursDetailsBySkip === false && this.sharedService.workHoursDetails) {
      const fourData = this.sharedService.workHoursDetails as { RegistrationServiceCategories?: SERVICE_FORM[] };
      this.myDataCollection = fourData.RegistrationServiceCategories ?? [];
    }

    if (this.businessTypes.length) {
      const firstId = this.businessTypes[0].Id;
      this.ensureCategoryEntry(firstId);
      this.currentBusinessTypeSelected = firstId;
      this.getService({ PagingEnabled: false, BusinessTypeId: firstId });
    }
  }

  onTabChange(index: number): void {
    const bt = this.businessTypes[index];
    if (!bt) return;
    this.onChangeCategory(bt.Id);
  }

  onCancelClick(): void {
    this.cancel.emit();
  }

  onSelectAllService(event: MatCheckboxChange): void {
    if (event.checked) {
      this.servicesList = this.servicesList.map((service) => ({
        ...service,
        checked: true,
      }));
    } else {
      this.servicesList = this.servicesList.map((service) => ({
        ...service,
        checked: false,
        ServicePrice: undefined,
      }));
    }
    this.servicesList.forEach((service) => {
      this.onChangeServiceCheckBox(service);
    });
  }

  incrementServicePrice(service: SERVICE): void {
    if (!service.checked) return;
    service.ServicePrice = (Number(service.ServicePrice) || 0) + 1;
    this.onChangeServiceCheckBox(service);
  }

  decrementServicePrice(service: SERVICE): void {
    if (!service.checked) return;
    const v = Number(service.ServicePrice) || 0;
    service.ServicePrice = v <= 1 ? 1 : v - 1;
    this.onChangeServiceCheckBox(service);
  }

  onStepperValueChange(service: SERVICE, value: number | string): void {
    if (!service.checked) return;
    const v = Number(value);
    if (!value && value !== 0) {
      service.ServicePrice = undefined;
    } else {
      service.ServicePrice = isNaN(v) || v < 1 ? 1 : v;
    }
    this.onChangeServiceCheckBox(service);
  }

  onChangeServiceCheckBox(service: SERVICE): void {
    if (service.checked) {
      service.ServicePrice =
        service.ServicePrice != null && service.ServicePrice !== undefined
          ? Number(service.ServicePrice)
          : 1;
      this.changeCategoryData('ADD', service);
    } else {
      this.changeCategoryData('REMOVE', service);
      service.ServicePrice = undefined;
    }
  }

  onChangeCategory(typeId: number): void {
    this.currentBusinessTypeSelected = typeId;
    this.selectAllServiceChecked = false;
    this.ensureCategoryEntry(typeId);
    this.getService({ PagingEnabled: false, BusinessTypeId: typeId });
  }

  private ensureCategoryEntry(typeId: number): void {
    if (
      !this.myDataCollection.map((item) => item.BusinessTypeId).includes(typeId)
    ) {
      this.myDataCollection.push({
        BusinessTypeId: typeId,
        RegistrationServices: [],
      });
    }
  }

  private getService(params: GET_SERVICES_INPUT): void {
    this.servicesLoading = true;
    this.businessDetailsService
      .getServices(params)
      .pipe(takeUntil(this.destroy$))
      .pipe(finalize(() => (this.servicesLoading = false)))
      .subscribe((res) => {
        const current = this.getCurrentCategoryForm();
        this.servicesList = (res.Result ?? []).map((service) => ({
          ...service,
          checked: current.RegistrationServices?.some(
            (s) => s.ServiceId === service.ServiceId
          ),
          ServicePrice:
            current.RegistrationServices?.find(
              (s) => s.ServiceId === service.ServiceId
            )?.ServicePrice ?? undefined,
        }));

        const anyNotChecked = this.servicesList.some(
          (service) => !service.checked
        );
        this.selectAllServiceChecked =
          !anyNotChecked && this.servicesList.length > 0;
      });
  }

  private getCurrentCategoryForm(): SERVICE_FORM {
    return (
      this.myDataCollection.find(
        (item) => item.BusinessTypeId === this.currentBusinessTypeSelected
      ) ?? { BusinessTypeId: this.currentBusinessTypeSelected, RegistrationServices: [] }
    );
  }

  private changeCategoryData(type: 'ADD' | 'REMOVE', service: SERVICE): void {
    const current = this.getCurrentCategoryForm();
    if (type === 'ADD') {
      if (
        current.RegistrationServices?.some(
          (item) => item.ServiceId === service.ServiceId
        )
      ) {
        const idx = current.RegistrationServices.findIndex(
          (item) => item.ServiceId === service.ServiceId
        );
        current.RegistrationServices[idx] = { ...service };
      } else {
        current.RegistrationServices = [
          ...(current.RegistrationServices ?? []),
          { ...service },
        ];
      }
    } else {
      current.RegistrationServices =
        current.RegistrationServices?.filter(
          (item) => item?.ServiceId !== service?.ServiceId
        ) ?? [];
    }

    const index = this.myDataCollection.findIndex(
      (item) => item.BusinessTypeId === current.BusinessTypeId
    );
    if (index === -1) {
      this.myDataCollection.push(current);
    } else {
      this.myDataCollection[index] = current;
    }
  }

  onSubmit(): void {
    const regId = this.sharedService.getCookie('RegistrationCycleLogId');
    if (!regId || this.loading) {
      if (!regId) {
        this.toastr.warning(this.translateService.instant('Registration_Session_Expired'));
        this.router.navigate(['registration-cycle']);
      }
      return;
    }

    this.loading = true;

    // Remove empty categories
    if (this.myDataCollection?.length > 1) {
      this.myDataCollection = this.myDataCollection.filter(
        (item) => item.RegistrationServices?.length > 0
      );
    }

    const formData: BUSINESS_SERVICE_INPUT = {
      RegistrationCycleLogId: String(regId),
      SavePhasesDataType: this.savePhasesDataType,
      RegistrationServiceCategories: this.myDataCollection,
    };

    for (const cat of formData.RegistrationServiceCategories ?? []) {
      if (
        !cat.RegistrationServices?.length ||
        cat.RegistrationServices.some(
          (s) => s.ServicePrice == null || s.ServicePrice === undefined
        )
      ) {
        this.loading = false;
        this.toastr.warning(
          this.translateService.instant('SERVICE_VALIDATION')
        );
        return;
      }
    }

    this.businessDetailsService
      .saveBusinessService(formData)
      .pipe(take(1))
      .subscribe(
        (res) => {
          this.loading = false;
          const ok = this.sharedService.checkResponseStatus(
            res.Message,
            res.State
          );
          if (ok) {
            this.sharedService.playSound('assets/sounds/done.mp3');
            this.confirm.emit();
          }
        },
        () => {
          this.loading = false;
        }
      );
  }
}

